import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    Alert,
    TextField,
    Stack,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Tooltip,
    Chip,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Link
} from '@mui/material';
import { Eye, SquarePen, Trash2, ExternalLink, RefreshCw, X } from 'lucide-react';
import { getJoinWithUs, getJoinWithUsById, updateJoinWithUs, deleteJoinWithUs } from '../../Services/JoinWithUs/JoinWithUsApi';
import PermissionWrapper from '../../Components/Auth/PermissionWrapper';
import { useGlobalSnackbar } from '../../Components/Common/GlobalSanckbar';
import { useUser } from '../../context/UserContext';
import GlobalPagination from '../../Components/Common/Globalpagination';

const statusColors = {
    pending: { bg: '#FFF8E1', color: '#B45309', label: 'Pending' },
    reviewed: { bg: '#F5F3FF', color: '#6B21A8', label: 'Reviewed' },
    contacted: { bg: '#EFF6FF', color: '#1D4ED8', label: 'Contacted' },
    joined: { bg: '#E8F5E8', color: '#2E7D32', label: 'Joined' },
    rejected: { bg: '#FFEBEE', color: '#C62828', label: 'Rejected' }
};

const JoinWithUs = () => {
    const { showSnackbar } = useGlobalSnackbar();
    const { user } = useUser();

    const [submissions, setSubmissions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Filters & Pagination
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [statusCounts, setStatusCounts] = useState({
        pending: 0,
        contacted: 0,
        joined: 0,
        rejected: 0
    });
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    const [limit, setLimit] = useState(10);

    // View Details Modal State
    const [openViewModal, setOpenViewModal] = useState(false);
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [loadingDetails, setLoadingDetails] = useState(false);

    // Edit Status & Remarks Modal State
    const [openEditModal, setOpenEditModal] = useState(false);
    const [editingRecord, setEditingRecord] = useState(null);
    const [editStatus, setEditStatus] = useState('pending');
    const [editRemarks, setEditRemarks] = useState('');
    const [updating, setUpdating] = useState(false);

    // Delete Confirmation Modal State
    const [openDeleteModal, setOpenDeleteModal] = useState(false);
    const [deletingRecord, setDeletingRecord] = useState(null);
    const [deleting, setDeleting] = useState(false);

    // Fetch List
    const fetchList = useCallback(async (querySearch = '', queryStatus = '', currentPage = 1, currentLimit = limit) => {
        try {
            setLoading(true);
            setError(null);
            const response = await getJoinWithUs(currentPage, currentLimit, querySearch, queryStatus);

            if (response && response.success) {
                setSubmissions(response.data || []);
                if (response.statusCounts) {
                    setStatusCounts(response.statusCounts);
                }
                const pag = response.pagination;
                if (pag) {
                    setTotalItems(pag.totalItems || 0);
                    setTotalPages(pag.totalPages || 1);
                } else {
                    setTotalItems(response.totalItems || response.total || (response.data ? response.data.length : 0));
                    setTotalPages(response.totalPages || 1);
                }
            } else {
                setSubmissions([]);
                setError("Failed to retrieve submissions.");
            }
        } catch (err) {
            console.error("Error fetching join with us list:", err);
            setError(err.response?.data?.message || "Failed to fetch submissions. Please try again.");
            setSubmissions([]);
        } finally {
            setLoading(false);
        }
    }, [limit]);

    // Debounce search function
    useEffect(() => {
        const handler = setTimeout(() => {
            fetchList(searchTerm, statusFilter, page, limit);
        }, 400);

        return () => clearTimeout(handler);
    }, [searchTerm, statusFilter, page, limit, fetchList]);

    // Open View Modal
    const handleOpenViewModal = async (record) => {
        setSelectedRecord(record);
        setOpenViewModal(true);
        setLoadingDetails(true);
        try {
            const res = await getJoinWithUsById(record.id);
            if (res && res.success && res.data) {
                setSelectedRecord(res.data);
            }
        } catch (err) {
            console.error("Failed to load details:", err);
        } finally {
            setLoadingDetails(false);
        }
    };

    // Open Edit Status & Remarks Modal
    const handleOpenEditModal = (record) => {
        setEditingRecord(record);
        setEditStatus(record.status || 'pending');
        setEditRemarks(record.remarks || '');
        setOpenEditModal(true);
    };

    // Submit Update Status & Remarks
    const handleSaveStatus = async () => {
        if (!editingRecord) return;
        setUpdating(true);
        try {
            const adminUser = user || JSON.parse(localStorage.getItem('user') || '{}');
            const payload = {
                status: editStatus,
                remarks: editRemarks,
                updatedByName: adminUser?.name || adminUser?.fullName || adminUser?.email || 'Admin'
            };
            const res = await updateJoinWithUs(editingRecord.id, payload);
            if (res && res.success) {
                showSnackbar("Submission status updated successfully!", "success");
                setOpenEditModal(false);
                fetchList(searchTerm, statusFilter, page);
            } else {
                showSnackbar(res?.message || "Failed to update submission", "error");
            }
        } catch (err) {
            console.error("Error updating submission:", err);
            showSnackbar(err.response?.data?.message || "Error updating submission status", "error");
        } finally {
            setUpdating(false);
        }
    };

    // Open Delete Modal
    const handleOpenDeleteModal = (record) => {
        setDeletingRecord(record);
        setOpenDeleteModal(true);
    };

    // Confirm Delete
    const handleDeleteRecord = async () => {
        if (!deletingRecord) return;
        setDeleting(true);
        try {
            const res = await deleteJoinWithUs(deletingRecord.id);
            if (res && res.success) {
                showSnackbar("Submission deleted successfully!", "success");
                setOpenDeleteModal(false);
                fetchList(searchTerm, statusFilter, page);
            } else {
                showSnackbar(res?.message || "Failed to delete submission", "error");
            }
        } catch (err) {
            console.error("Error deleting submission:", err);
            showSnackbar(err.response?.data?.message || "Error deleting submission", "error");
        } finally {
            setDeleting(false);
        }
    };

    const renderStatusChip = (statusKey) => {
        const config = statusColors[statusKey?.toLowerCase()] || { bg: '#F1F5F9', color: '#475569', label: statusKey || 'Pending' };
        return (
            <Chip
                label={config.label}
                size="small"
                sx={{
                    bgcolor: config.bg,
                    color: config.color,
                    fontWeight: 500,
                    fontSize: '0.7rem',
                    height: '22px',
                    borderRadius: '4px'
                }}
            />
        );
    };

    return (
        <PermissionWrapper permission="view">
            <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#FBFBFB', minHeight: '100vh' }}>
                {/* Header Section */}
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        justifyContent: 'space-between',
                        alignItems: { xs: 'flex-start', sm: 'center' },
                        mb: 3,
                        gap: 2
                    }}
                >
                    <Typography sx={{ color: '#1E293B', fontSize: { xs: '1.25rem', md: '1.4rem' }, fontWeight: 500 }}>
                        Join With Us Submissions
                    </Typography>

                    {/* Filter & Search Bar */}
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        flexWrap: 'wrap',
                        width: { xs: '100%', sm: 'auto' }
                    }}>
                        <FormControl size="small" sx={{ minWidth: 130, bgcolor: 'white' }}>
                            <InputLabel sx={{ fontSize: '0.8rem', color: '#64748B', '&.Mui-focused': { color: '#FFBD65' } }}>Status Filter</InputLabel>
                            <Select
                                value={statusFilter}
                                label="Status Filter"
                                onChange={(e) => {
                                    setStatusFilter(e.target.value);
                                    setPage(1);
                                }}
                                sx={{
                                    borderRadius: '6px',
                                    height: '38px',
                                    fontSize: '0.8rem',
                                    '& fieldset': { borderColor: '#E2E8F0' },
                                    '&:hover fieldset': { borderColor: '#CBD5E1' },
                                    '&.Mui-focused fieldset': { borderColor: '#FFBD65', borderWidth: '1px' },
                                }}
                            >
                                <MenuItem value="" sx={{ fontSize: '0.8rem' }}>All Statuses</MenuItem>
                                <MenuItem value="pending" sx={{ fontSize: '0.8rem' }}>Pending</MenuItem>
                                <MenuItem value="contacted" sx={{ fontSize: '0.8rem' }}>Contacted</MenuItem>
                                <MenuItem value="joined" sx={{ fontSize: '0.8rem' }}>Joined</MenuItem>
                                <MenuItem value="rejected" sx={{ fontSize: '0.8rem' }}>Rejected</MenuItem>
                            </Select>
                        </FormControl>

                        <TextField
                            placeholder="Search by name, email, phone..."
                            value={searchTerm}
                            onChange={(e) => {
                                setSearchTerm(e.target.value);
                                setPage(1);
                            }}
                            variant="outlined"
                            size="small"
                            sx={{
                                width: { xs: '100%', sm: 220 },
                                bgcolor: '#FFFFFF',
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '6px',
                                    height: '38px',
                                    fontSize: '0.8rem',
                                    '& fieldset': { borderColor: '#E2E8F0' },
                                    '&:hover fieldset': { borderColor: '#CBD5E1' },
                                    '&.Mui-focused fieldset': { borderColor: '#FFBD65', borderWidth: '1px' },
                                },
                            }}
                        />

                        <IconButton
                            disableFocusRipple disableRipple disableTouchRipple
                            onClick={() => fetchList(searchTerm, statusFilter, page)}
                            sx={{
                                bgcolor: '#FFFFFF',
                                border: '1px solid #E2E8F0',
                                borderRadius: '6px',
                                width: 38,
                                height: 38,
                                color: '#64748B',
                                '&:hover': { bgcolor: '#F8FAFC' }
                            }}
                        >
                            <RefreshCw size={16} />
                        </IconButton>
                    </Box>
                </Box>

                {/* Status Summary Cards */}
                <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                    {[
                        { label: 'All Submissions', value: totalItems || 0, status: '', color: '#64748B', activeBg: '#F8FAFC', activeBorder: '#64748B' },
                        { label: 'Pending', value: statusCounts.pending || 0, status: 'pending', color: '#B45309', activeBg: '#FFFBEB', activeBorder: '#F59E0B' },
                        { label: 'Contacted', value: statusCounts.contacted || 0, status: 'contacted', color: '#1D4ED8', activeBg: '#EFF6FF', activeBorder: '#3B82F6' },
                        { label: 'Joined', value: statusCounts.joined || 0, status: 'joined', color: '#15803D', activeBg: '#F0FDF4', activeBorder: '#22C55E' },
                        { label: 'Rejected', value: statusCounts.rejected || 0, status: 'rejected', color: '#B91C1C', activeBg: '#FEF2F2', activeBorder: '#EF4444' }
                    ].map((card) => {
                        const isActive = statusFilter === card.status;
                        return (
                            <Paper
                                key={card.label}
                                elevation={0}
                                onClick={() => {
                                    setStatusFilter(card.status);
                                    setPage(1);
                                }}
                                sx={{
                                    flex: '1 1 120px',
                                    minWidth: '120px',
                                    p: 1.5,
                                    borderRadius: '6px',
                                    bgcolor: isActive ? card.activeBg : '#FFFFFF',
                                    border: isActive ? `1px solid ${card.activeBorder}` : '1px solid #E2E8F0',
                                    cursor: 'pointer',
                                    boxShadow: 'none',
                                }}
                            >
                                <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: card.color, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                    {card.label}
                                </Typography>
                                <Typography sx={{ fontSize: '1.25rem', fontWeight: 600, color: '#0F172A', mt: 0.5 }}>
                                    {card.value}
                                </Typography>
                            </Paper>
                        );
                    })}
                </Box>

                {error && <Alert severity="error" sx={{ mb: 3, borderRadius: '6px' }} onClose={() => setError(null)}>{error}</Alert>}

                {/* Submissions Table */}
                <Paper sx={{ borderRadius: "6px", border: "1px solid #E2E8F0", overflow: "hidden", bgcolor: '#FFFFFF' }} elevation={0}>
                    <TableContainer>
                        <Table sx={{ width: '100%', tableLayout: 'auto' }}>
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#F8FAFC' }}>
                                    <TableCell align="left" sx={{ py: 1.5, px: 1.5, fontSize: '0.7rem', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#475569', whiteSpace: 'nowrap' }}>S.No</TableCell>
                                    <TableCell align="left" sx={{ py: 1.5, px: 1.5, fontSize: '0.7rem', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#475569', whiteSpace: 'nowrap' }}>Full Name</TableCell>
                                    <TableCell align="left" sx={{ py: 1.5, px: 1.5, fontSize: '0.7rem', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#475569', whiteSpace: 'nowrap' }}>Email</TableCell>
                                    <TableCell align="left" sx={{ py: 1.5, px: 1.5, fontSize: '0.7rem', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#475569', whiteSpace: 'nowrap' }}>Phone</TableCell>
                                    <TableCell align="left" sx={{ py: 1.5, px: 1.5, fontSize: '0.7rem', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#475569', whiteSpace: 'nowrap' }}>Interested In</TableCell>
                                    <TableCell align="left" sx={{ py: 1.5, px: 1.5, fontSize: '0.7rem', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#475569', whiteSpace: 'nowrap' }}>Status</TableCell>
                                    <TableCell align="left" sx={{ py: 1.5, px: 1.5, fontSize: '0.7rem', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#475569', whiteSpace: 'nowrap' }}>Date</TableCell>
                                    <TableCell align="right" sx={{ py: 1.5, px: 1.5, fontSize: '0.7rem', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#475569', whiteSpace: 'nowrap' }}>Actions</TableCell>
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {loading ? (
                                    <TableRow>
                                        <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                                            <CircularProgress size={24} sx={{ color: '#FFBD65' }} />
                                        </TableCell>
                                    </TableRow>
                                ) : submissions.length > 0 ? (
                                    submissions.map((row, idx) => (
                                        <TableRow key={row.id} sx={{
                                            '&:last-child td': { borderBottom: 0 }
                                        }}>
                                            <TableCell align="left" sx={{ py: 1.25, px: 1.5, fontSize: '0.75rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                                {(page - 1) * limit + idx + 1}
                                            </TableCell>
                                            <TableCell align="left" sx={{ py: 1.25, px: 1.5, fontSize: '0.75rem', fontWeight: 500, color: '#1E293B', whiteSpace: 'nowrap' }}>
                                                {row.fullName || '—'}
                                            </TableCell>
                                            <TableCell align="left" sx={{ py: 1.25, px: 1.5, fontSize: '0.75rem', color: '#475569', whiteSpace: 'nowrap' }}>
                                                {row.email || '—'}
                                            </TableCell>
                                            <TableCell align="left" sx={{ py: 1.25, px: 1.5, fontSize: '0.75rem', color: '#475569', whiteSpace: 'nowrap' }}>
                                                {row.phone || '—'}
                                            </TableCell>
                                            <TableCell align="left" sx={{ py: 1.25, px: 1.5, fontSize: '0.75rem', color: '#1E293B', fontWeight: 500, whiteSpace: 'nowrap' }}>
                                                {row.interestedIn || '—'}
                                            </TableCell>
                                            <TableCell align="left" sx={{ py: 1.25, px: 1.5, whiteSpace: 'nowrap' }}>
                                                {renderStatusChip(row.status)}
                                            </TableCell>
                                            <TableCell align="left" sx={{ py: 1.25, px: 1.5, fontSize: '0.75rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                                {row.createdAt ? new Date(row.createdAt).toLocaleDateString() : '—'}
                                            </TableCell>
                                            <TableCell align="right" sx={{ py: 1.25, px: 1.5, whiteSpace: 'nowrap' }}>
                                                <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                                                    <Tooltip title="View Details">
                                                        <IconButton
                                                            size="small"
                                                            disableFocusRipple disableRipple disableTouchRipple
                                                            sx={{ color: '#64748B', p: 0.5 }}
                                                            onClick={() => handleOpenViewModal(row)}
                                                        >
                                                            <Eye size={16} />
                                                        </IconButton>
                                                    </Tooltip>

                                                    <PermissionWrapper permission="edit" mode="disable">
                                                        <Tooltip title="Update Status">
                                                            <IconButton
                                                                size="small"
                                                                disableFocusRipple disableRipple disableTouchRipple
                                                                sx={{ color: '#FFBD65', p: 0.5 }}
                                                                onClick={() => handleOpenEditModal(row)}
                                                            >
                                                                <SquarePen size={16} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </PermissionWrapper>

                                                    <PermissionWrapper permission="delete" mode="disable">
                                                        <Tooltip title="Delete">
                                                            <IconButton
                                                                size="small"
                                                                disableFocusRipple disableRipple disableTouchRipple
                                                                sx={{ color: '#EF4444', p: 0.5 }}
                                                                onClick={() => handleOpenDeleteModal(row)}
                                                            >
                                                                <Trash2 size={16} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </PermissionWrapper>
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={8} align="center" sx={{ py: 6, color: '#64748B', fontSize: '0.75rem' }}>
                                            {searchTerm || statusFilter ? 'No submissions found matching your filters.' : 'No submissions found.'}
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>

                {/* Pagination */}
                <GlobalPagination
                    page={page}
                    pageSize={limit}
                    totalRecords={totalItems}
                    totalPages={totalPages}
                    onPageChange={(newPage) => setPage(newPage)}
                    onPageSizeChange={(newLimit) => {
                        setLimit(newLimit);
                        setPage(1);
                    }}
                    loading={loading}
                />

                {/* VIEW DETAILS MODAL */}
                <Dialog
                    open={openViewModal}
                    onClose={() => setOpenViewModal(false)}
                    maxWidth="sm"
                    fullWidth
                    PaperProps={{
                        sx: {
                            borderRadius: '8px',
                            border: '1px solid #E2E8F0',
                            boxShadow: 'none',
                            bgcolor: '#FFFFFF',
                            maxWidth: 520
                        }
                    }}
                >
                    <Box sx={{ px: 2.5, pt: 2.5, pb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #F1F5F9' }}>
                        <Typography sx={{ fontSize: '0.95rem', fontWeight: 500, color: '#1E293B' }}>
                            Submission Details
                        </Typography>
                        <IconButton
                            size="small"
                            disableFocusRipple disableRipple disableTouchRipple
                            onClick={() => setOpenViewModal(false)}
                            sx={{ color: '#64748B', p: 0.5, '&:hover': { color: '#1E293B' } }}
                        >
                            <X size={16} />
                        </IconButton>
                    </Box>

                    <DialogContent sx={{ p: 2.5 }}>
                        {loadingDetails ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
                                <CircularProgress size={24} sx={{ color: '#FFBD65' }} />
                            </Box>
                        ) : selectedRecord ? (
                            <Stack spacing={2}>
                                {/* Overview Card */}
                                <Box sx={{ bgcolor: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: '6px', p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Box>
                                        <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.25 }}>
                                            Candidate Name
                                        </Typography>
                                        <Typography sx={{ fontSize: '1rem', fontWeight: 500, color: '#1E293B' }}>
                                            {selectedRecord.fullName || '—'}
                                        </Typography>
                                        <Typography sx={{ fontSize: '0.75rem', color: '#64748B', mt: 0.25 }}>
                                            Interested in: <strong style={{ color: '#1E293B' }}>{selectedRecord.interestedIn || 'General'}</strong>
                                        </Typography>
                                    </Box>
                                    <Box>{renderStatusChip(selectedRecord.status)}</Box>
                                </Box>

                                {/* Contact Details Table Card */}
                                <Box sx={{ border: '1px solid #E2E8F0', borderRadius: '6px', overflow: 'hidden', bgcolor: '#FFFFFF' }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, py: 1.25, borderBottom: '1px solid #F1F5F9' }}>
                                        <Typography sx={{ fontSize: '0.75rem', color: '#64748B' }}>Email Address</Typography>
                                        <Typography sx={{ fontSize: '0.75rem', color: '#1976D2', fontWeight: 500 }}>
                                            {selectedRecord.email ? (
                                                <Link href={`mailto:${selectedRecord.email}`} underline="hover" color="inherit">
                                                    {selectedRecord.email}
                                                </Link>
                                            ) : '—'}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, py: 1.25, borderBottom: selectedRecord.portfolioLink ? '1px solid #F1F5F9' : 'none' }}>
                                        <Typography sx={{ fontSize: '0.75rem', color: '#64748B' }}>Phone Number</Typography>
                                        <Typography sx={{ fontSize: '0.75rem', color: '#1E293B', fontWeight: 500 }}>
                                            {selectedRecord.phone ? (
                                                <Link href={`tel:${selectedRecord.phone}`} underline="hover" color="inherit">
                                                    {selectedRecord.phone}
                                                </Link>
                                            ) : '—'}
                                        </Typography>
                                    </Box>
                                    {selectedRecord.portfolioLink && (
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, py: 1.25 }}>
                                            <Typography sx={{ fontSize: '0.75rem', color: '#64748B' }}>Portfolio / Website</Typography>
                                            <Link
                                                href={selectedRecord.portfolioLink.startsWith('http') ? selectedRecord.portfolioLink : `https://${selectedRecord.portfolioLink}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                sx={{ fontSize: '0.75rem', display: 'inline-flex', alignItems: 'center', gap: 0.5, color: '#1976D2', fontWeight: 500 }}
                                            >
                                                View Link <ExternalLink size={13} />
                                            </Link>
                                        </Box>
                                    )}
                                </Box>

                                {/* About Yourself */}
                                {selectedRecord.aboutYourself && (
                                    <Box>
                                        <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.5 }}>
                                            About Candidate
                                        </Typography>
                                        <Box sx={{ bgcolor: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: '6px', p: 1.5 }}>
                                            <Typography sx={{ fontSize: '0.8rem', color: '#334155', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
                                                {selectedRecord.aboutYourself}
                                            </Typography>
                                        </Box>
                                    </Box>
                                )}

                                {/* Admin Remarks */}
                                <Box>
                                    <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.5 }}>
                                        Admin Remarks
                                    </Typography>
                                    <Box sx={{ bgcolor: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: '6px', p: 1.5 }}>
                                        <Typography sx={{ fontSize: '0.8rem', color: selectedRecord.remarks ? '#334155' : '#94A3B8', fontStyle: selectedRecord.remarks ? 'normal' : 'italic' }}>
                                            {selectedRecord.remarks || 'No remarks recorded yet.'}
                                        </Typography>
                                    </Box>
                                </Box>

                                {/* Timestamps */}
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt: 1, borderTop: '1px solid #F1F5F9' }}>
                                    <Typography sx={{ fontSize: '0.72rem', color: '#94A3B8' }}>
                                        Submitted: {selectedRecord.createdAt ? new Date(selectedRecord.createdAt).toLocaleString() : '—'}
                                    </Typography>
                                    <Typography sx={{ fontSize: '0.72rem', color: '#94A3B8' }}>
                                        Updated by: <strong style={{ color: '#475569' }}>{selectedRecord.updatedByName || selectedRecord.updatedBy || 'Admin'}</strong>
                                    </Typography>
                                </Box>
                            </Stack>
                        ) : null}
                    </DialogContent>

                    <DialogActions sx={{ px: 2.5, py: 2, borderTop: '1px solid #F1F5F9', display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
                        <Button
                            variant="outlined"
                            disableElevation disableFocusRipple disableRipple
                            onClick={() => setOpenViewModal(false)}
                            sx={{
                                borderColor: '#E2E8F0',
                                borderRadius: '6px',
                                fontSize: '0.8rem',
                                fontWeight: 500,
                                color: '#475569',
                                textTransform: 'none',
                                px: 2.5,
                                py: 0.75,
                                '&:hover': { borderColor: '#CBD5E1', bgcolor: '#F8FAFC' }
                            }}
                        >
                            Close
                        </Button>
                        <Button
                            variant="contained"
                            disableElevation disableFocusRipple disableRipple
                            onClick={() => {
                                setOpenViewModal(false);
                                handleOpenEditModal(selectedRecord);
                            }}
                            sx={{
                                bgcolor: '#FFBD65',
                                color: '#000000',
                                borderRadius: '6px',
                                fontSize: '0.8rem',
                                fontWeight: 500,
                                textTransform: 'none',
                                boxShadow: 'none',
                                px: 2.5,
                                py: 0.75
                            }}
                        >
                            Update Status & Remarks
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* EDIT STATUS & REMARKS MODAL */}
                <Dialog
                    open={openEditModal}
                    onClose={() => setOpenEditModal(false)}
                    maxWidth="xs"
                    fullWidth
                    PaperProps={{
                        sx: {
                            borderRadius: '8px',
                            border: '1px solid #E2E8F0',
                            boxShadow: 'none',
                            bgcolor: '#FFFFFF',
                            maxWidth: 440
                        }
                    }}
                >
                    <Box sx={{ px: 2.5, pt: 2.5, pb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #F1F5F9' }}>
                        <Typography sx={{ fontSize: '0.95rem', fontWeight: 500, color: '#1E293B' }}>
                            Update Submission Status
                        </Typography>
                        <IconButton
                            size="small"
                            disableFocusRipple disableRipple disableTouchRipple
                            onClick={() => setOpenEditModal(false)}
                            sx={{ color: '#64748B', p: 0.5, '&:hover': { color: '#1E293B' } }}
                        >
                            <X size={16} />
                        </IconButton>
                    </Box>

                    <DialogContent sx={{ p: 2.5 }}>
                        <Box sx={{ bgcolor: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: '6px', p: 1.5, mb: 2 }}>
                            <Typography sx={{ fontSize: '0.72rem', color: '#64748B' }}>Updating submission for:</Typography>
                            <Typography sx={{ fontSize: '0.85rem', fontWeight: 500, color: '#1E293B', mt: 0.25 }}>
                                {editingRecord?.fullName} ({editingRecord?.email || 'No email'})
                            </Typography>
                        </Box>

                        <Stack spacing={2}>
                            <FormControl fullWidth size="small">
                                <InputLabel sx={{ fontSize: '0.8rem', color: '#64748B', '&.Mui-focused': { color: '#FFBD65' } }}>Status</InputLabel>
                                <Select
                                    value={editStatus}
                                    label="Status"
                                    onChange={(e) => setEditStatus(e.target.value)}
                                    sx={{
                                        borderRadius: '6px',
                                        fontSize: '0.8rem',
                                        '& fieldset': { borderColor: '#E2E8F0' },
                                        '&:hover fieldset': { borderColor: '#CBD5E1' },
                                        '&.Mui-focused fieldset': { borderColor: '#FFBD65', borderWidth: '1px' }
                                    }}
                                >
                                    <MenuItem value="pending" sx={{ fontSize: '0.8rem' }}>Pending</MenuItem>
                                    <MenuItem value="contacted" sx={{ fontSize: '0.8rem' }}>Contacted</MenuItem>
                                    <MenuItem value="joined" sx={{ fontSize: '0.8rem' }}>Joined</MenuItem>
                                    <MenuItem value="rejected" sx={{ fontSize: '0.8rem' }}>Rejected</MenuItem>
                                </Select>
                            </FormControl>

                            <TextField
                                fullWidth
                                multiline
                                rows={3}
                                label="Remarks / Internal Notes"
                                placeholder="Enter admin remarks or notes (e.g. Contacted candidate on phone. Selected as partner instructor.)"
                                value={editRemarks}
                                onChange={(e) => setEditRemarks(e.target.value)}
                                size="small"
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '6px',
                                        fontSize: '0.8rem',
                                        '& fieldset': { borderColor: '#E2E8F0' },
                                        '&:hover fieldset': { borderColor: '#CBD5E1' },
                                        '&.Mui-focused fieldset': { borderColor: '#FFBD65', borderWidth: '1px' }
                                    },
                                    '& .MuiInputLabel-root': { fontSize: '0.8rem', color: '#64748B' },
                                    '& .MuiInputLabel-root.Mui-focused': { color: '#FFBD65' }
                                }}
                            />
                        </Stack>
                    </DialogContent>

                    <DialogActions sx={{ px: 2.5, py: 2, borderTop: '1px solid #F1F5F9', display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
                        <Button
                            variant="outlined"
                            disableElevation disableFocusRipple disableRipple
                            onClick={() => setOpenEditModal(false)}
                            sx={{
                                borderColor: '#E2E8F0',
                                borderRadius: '6px',
                                fontSize: '0.8rem',
                                fontWeight: 500,
                                color: '#475569',
                                textTransform: 'none',
                                px: 2.5,
                                py: 0.75,
                                '&:hover': { borderColor: '#CBD5E1', bgcolor: '#F8FAFC' }
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleSaveStatus}
                            disabled={updating}
                            variant="contained"
                            disableElevation disableFocusRipple disableRipple
                            sx={{
                                bgcolor: '#FFBD65',
                                color: '#000000',
                                textTransform: 'none',
                                fontSize: '0.8rem',
                                fontWeight: 500,
                                borderRadius: '6px',
                                px: 3,
                                py: 0.75
                            }}
                        >
                            {updating ? <CircularProgress size={18} color="inherit" /> : 'Save Changes'}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* DELETE CONFIRMATION DIALOG */}
                <Dialog
                    open={openDeleteModal}
                    onClose={() => setOpenDeleteModal(false)}
                    maxWidth="xs"
                    fullWidth
                    PaperProps={{
                        sx: {
                            borderRadius: '8px',
                            border: '1px solid #E2E8F0',
                            boxShadow: 'none'
                        }
                    }}
                >
                    <DialogTitle sx={{ fontWeight: 500, color: '#C62828', fontSize: '1rem', pb: 1 }}>
                        Confirm Deletion
                    </DialogTitle>
                    <DialogContent>
                        <Typography sx={{ color: '#475569', fontSize: '0.85rem' }}>
                            Are you sure you want to delete the submission from <strong>{deletingRecord?.fullName}</strong>? This action cannot be undone.
                        </Typography>
                    </DialogContent>
                    <DialogActions sx={{ px: 2.5, py: 2, borderTop: '1px solid #F1F5F9', display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
                        <Button
                            variant="outlined"
                            disableElevation disableFocusRipple disableRipple
                            onClick={() => setOpenDeleteModal(false)}
                            sx={{
                                borderColor: '#E2E8F0',
                                borderRadius: '6px',
                                fontSize: '0.8rem',
                                fontWeight: 500,
                                color: '#475569',
                                textTransform: 'none',
                                px: 2.5,
                                py: 0.75,
                                '&:hover': { borderColor: '#CBD5E1', bgcolor: '#F8FAFC' }
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleDeleteRecord}
                            disabled={deleting}
                            variant="contained"
                            color="error"
                            disableElevation disableFocusRipple disableRipple
                            sx={{
                                textTransform: 'none',
                                fontSize: '0.8rem',
                                fontWeight: 500,
                                borderRadius: '6px',
                                px: 3,
                                py: 0.75
                            }}
                        >
                            {deleting ? <CircularProgress size={18} color="inherit" /> : 'Delete'}
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
        </PermissionWrapper>
    );
};

export default JoinWithUs;
